import * as Sentry from '@sentry/nextjs';
import { useMutation as useReactMutation } from '@tanstack/react-query';

interface IParams<TResult> {
  method?: 'POST' | 'GET' | 'PUT' | 'DELETE';
  onSuccess?: (result: TResult) => void;
  onError?: (error: string) => void;
}

const isJsonString = (str: string) => {
  try {
    const obj = JSON.parse(str);

    if (obj && typeof obj === 'object') {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

export const useMutation = <
  TResult extends Record<string, unknown> | undefined,
  TInput extends Record<string, unknown> | undefined | unknown = unknown,
>(
  url: string,
  { method, onSuccess, onError }: IParams<TResult>,
) =>
  useReactMutation<TResult, Error, TInput>(
    data =>
      fetch(process.env.NEXT_PUBLIC_PROXY_URL ?? '', {
        method: 'POST',
        cache: 'no-cache',
        redirect: 'follow',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: method ?? 'POST',
          path: url,
          body: data ?? null,
        }),
      }).then(async response => {
        const responseText = await response.text();
        const data = (isJsonString(responseText) ? JSON.parse(responseText) : {}) as TResult;

        if (response.status >= 400) {
          if (data && 'errors' in data && Array.isArray(data.errors)) {
            throw new Error(data.errors[0]);
          }

          throw new Error(
            data && 'error' in data && typeof data.error === 'string' ? data.error : 'Server error',
          );
        }

        return data;
      }),
    {
      onSuccess,
      onError: error => {
        onError?.(error.message);
        Sentry.captureException(error);
        return error;
      },
    },
  );
